import classNames from "classnames";
import React, {
  ForwardedRef,
  forwardRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Calendar } from "../calendar/Calendar";
import { withoutTime, withoutTimeStr } from "../calendar/calendar.util";
import { CalendarPopover } from "../calendar-popover/CalendarPopover";
import { DateInputField } from "../date-input-field/DateInputField";
import { DatePickerProps } from "./DateInputWithPicker.types";
import { handleInvalidDate } from "./util/datePicker.util";
import { sleep } from "../../utils/sleep";
import { makeDateHtmlValue } from "../date-input-field/util/DateInputField.util";
import { useCombinedRefs } from "../../utils/useCombinedRefs";
import { getBreakpoint, isSmallScreen } from "../../utils";

export const DateInputWithPicker = forwardRef(
  (
    {
      label,
      className,
      onBlur,
      minDate,
      maxDate,
      validateMethod,
      onValidate,
      error,
      status,
      statusMessage,
      defaultValue,
      value: dateProp,
      onChange: onDateChange,
      range,
      onRangeChange,
      format,
      showDateAsText,
      displayDateTextMethod,
      ariaCalendarPopoverLabel = "Show Calendar. Only available dates are displayed.",
      YYYYPlaceholderText,
      MMPlaceholderText,
      DDPlaceholderText,
      DDAriaLabel = `${label} day`,
      MMAriaLabel = `${label} month`,
      YYYYAriaLabel = `${label} year`,
      advanceByMonthAndYear,
      monthsList,
      yearsList,
      notes,
      daysDisabled,
      showTodayButton,
      showTodayButtonLabel = "Select Today",
      maxDateErrorLabel,
      daysDisabledErrorLabel,
      minDateErrorLabel,
      ariaNextMonthLabel = "Next month",
      ariaPreviousMonthLabel = "Previous month",
      calendarMonthsNavAriaLabel = "Months",
      separator,
      disabled,
      visibleCount: visibleCountProp = 1,
      hideDaysOutsideCurrentMonth = false,
      navigateToDisabledMonths = true,
      labelRef: labelRefExternal,
      portalWrapperClassName,
      anchorX = "center",
      anchorY = "top",
    }: DatePickerProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [hasDateInputDisabledDate, setHasDateInputDisabledDate] =
      useState(false);
    const [visibleCount, setVisibleCount] = useState(visibleCountProp);

    const [isRangeStartInput, setIsRangeStartInput] = useState(false);
    const [isRangeEndInput, setIsRangeEndInput] = useState(false);

    const isRangeControlled = useRef(Array.isArray(range));
    const prevRange = useRef<(Date | null | undefined)[] | undefined>(
      undefined
    );

    const labelRef = useRef<HTMLLabelElement>(null as any);
    useCombinedRefs(labelRef, labelRefExternal ?? null);

    // Force the number of "visibleCount" to be equal to 1 if XS or SM breakpoint
    const _onResize = useCallback((): void => {
      const breakPoint = getBreakpoint(window.innerWidth);
      setVisibleCount(isSmallScreen(breakPoint) ? 1 : visibleCountProp);
    }, [visibleCountProp]);

    useEffect(() => {
      window.addEventListener("resize", _onResize);
      return (): void => {
        window.removeEventListener("resize", _onResize);
      };
    }, [_onResize]);

    // Detect if date inputs are part of range group
    // @TODO: use context to get below informations
    useEffect(() => {
      const dateInput = labelRef.current;
      const dateInputSibling = labelRef.current?.nextSibling;
      const isRangeInput = dateInput?.classList.contains(
        "spark-input-group__item"
      );
      const isSiblingRangeInput = (
        dateInputSibling as HTMLElement
      )?.classList.contains("spark-input-group__item");

      if (isRangeInput) {
        setIsRangeEndInput(true);
      }

      if (isRangeInput && isSiblingRangeInput) {
        setIsRangeEndInput(false);
        setIsRangeStartInput(true);
      }
    }, []);

    useEffect(() => {
      const parentElement = labelRef.current?.parentElement;
      const changeSource = parentElement?.getAttribute("data-change-source");

      // Handle automatic popover opening for range variant, when second date is empty
      if (changeSource === "calendar") {
        if (!range?.[0] && !!range?.[1]) {
          setActiveIcon(isRangeStartInput);
          setTimeout(() => setCalOpen(isRangeStartInput), 0);
        }
        if (!range?.[1] && !!range?.[0]) {
          setActiveIcon(isRangeEndInput);
          setTimeout(() => setCalOpen(isRangeEndInput), 0);
        }
      }

      // Handle situation when manually typed rangeEnd < rangeStart
      if (isRangeControlled.current && range && changeSource !== "calendar") {
        const [newRangeStart, newRangeEnd] = range;

        if (!!newRangeEnd && !!newRangeStart) {
          // Handle situation when range START changes and rangeStart > rangeEnd
          if (
            !prevRange.current?.[0] ||
            (!!prevRange.current?.[0] &&
              withoutTime(newRangeStart).getTime() !==
                withoutTime(prevRange.current?.[0]).getTime())
          ) {
            if (withoutTime(newRangeStart) > withoutTime(newRangeEnd)) {
              prevRange.current = range;
              onRangeChange?.([newRangeStart, null]);
            }
          }

          // Handle situation when range END changes and rangeEnd < rangeStart
          if (
            !prevRange.current?.[1] ||
            (!!prevRange.current?.[1] &&
              withoutTime(newRangeEnd).getTime() !==
                withoutTime(prevRange.current?.[1]).getTime())
          ) {
            if (withoutTime(newRangeEnd) < withoutTime(newRangeStart)) {
              prevRange.current = range;
              onRangeChange?.([null, newRangeEnd]);
            }
          }
        }
      }
      // as a rule, remove callbacks from deps to prevent unexpected re-renders
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isRangeEndInput, isRangeStartInput, range]);

    const [dateState, setDateState] = useState<Date | undefined | null>(
      defaultValue
    );

    // Override dateProp with range value (cast Boolean value to array indexe, 0 or 1)
    dateProp = isRangeControlled.current
      ? range?.[isRangeStartInput ? 0 : 1]
      : dateProp;

    const isControlled = dateProp !== undefined;
    const date = isControlled ? dateProp : dateState;

    const [prevDateChange, setPrevDateChange] = useState<string | null>();

    const iconRef = useRef<HTMLElement | null>(
      null
    ) as MutableRefObject<HTMLElement>;
    const popoverRef = useRef<HTMLDivElement>(null as any);

    const [calOpen, setCalOpen] = useState<boolean>(false);
    const [active, setActive] = useState<boolean>(calOpen || false);
    const [focus, setFocus] = useState<boolean>(false);
    const focusRef = useRef(focus);
    const [activeIcon, setActiveIcon] = useState<boolean>(false);
    const calendarActiveRef = useRef(activeIcon);
    const [isDateInputInvalidDate, setIsDateInputInvalidDate] =
      useState<boolean>(false);
    const [isDateInputValueUndefined, setIsDateInputValueUndefined] =
      useState<boolean>(false);

    const iconClassName = classNames("spark-icon-calendar", "spark-icon--md", {
      "spark-icon--fill": activeIcon,
    });

    useEffect(() => {
      if (visibleCount === 2 && calOpen) {
        setActive(true);
      }
    }, [calOpen, visibleCount]);

    const handleBlur = useCallback(async (): Promise<any> => {
      setFocus(false);
      await sleep(10);
      onBlur?.(date ?? null);
    }, [date, onBlur]);

    useEffect(() => {
      calendarActiveRef.current = activeIcon;
    }, [activeIcon]);

    useEffect(() => {
      focusRef.current = focus;
    }, [focus]);

    useEffect(() => {
      !active && handleBlur();
    }, [active, handleBlur]);

    const assignRefValue = (value?: string): void => {
      if (!ref) {
        return;
      } else {
        // @ts-ignore
        ref.current.value = value;
      }
    };

    /**
     * On selecting the date in the calendar popover, the same value is set to state which later triggers the date Change event
     * @param dateFromPicker
     */
    const handleDatePickerSelection = (dateFromPicker?: Date | null): void => {
      setDateState(dateFromPicker);
      if (dateFromPicker) {
        assignRefValue(
          makeDateHtmlValue(
            String(dateFromPicker.getFullYear()),
            String(dateFromPicker.getMonth() + 1),
            String(dateFromPicker.getDate())
          )
        );
      } else {
        assignRefValue(undefined);
      }

      onDateChange?.(dateFromPicker);
    };

    /**
     * When the date parts are manually entered, the handleDateChange is triggered.
     * But the value is set when date is not same as prev date
     * @param dateInputValue
     */
    const handleDateChange = (dateInputValue?: Date | null): void => {
      setDateState(dateInputValue);

      if (dateInputValue !== undefined) {
        setIsDateInputValueUndefined(false);

        if (prevDateChange !== undefined) {
          setPrevDateChange(undefined);
          onDateChange?.(undefined);
          return;
        }

        // @TODO: Invalid date can only come from DateInputField, so this validation should be moved there
        /**
         * Case 1: Restrict infinite loop when date is not valid
         */
        if (dateInputValue?.toString() === "Invalid Date") {
          const dateAndArgNotEqual =
            !!date &&
            !!dateInputValue &&
            withoutTimeStr(date) !== withoutTimeStr(dateInputValue);

          if (!isDateInputInvalidDate && dateAndArgNotEqual) {
            console.warn("date invalid");
            setIsDateInputInvalidDate(true);
            onDateChange?.(undefined);
            return;
          } else if (isDateInputInvalidDate) {
            return;
          }
        } else {
          /**
           * Case 2: Manually edit the field & values to be updated
           */
          setIsDateInputInvalidDate(false);
          const isDateAndDateFromChildNotEqual =
            date &&
            dateInputValue &&
            withoutTimeStr(date) !== withoutTimeStr(dateInputValue);

          if (
            isDateAndDateFromChildNotEqual ||
            date === null ||
            date === undefined ||
            dateInputValue === null
          ) {
            prevRange.current = range;
            isRangeControlled.current
              ? onRangeChange?.([
                  isRangeStartInput ? dateInputValue : range?.[0],
                  isRangeEndInput ? dateInputValue : range?.[1],
                ])
              : onDateChange?.(dateInputValue);
          }
        }
      } else {
        /**
         * Case 3: Restrict infinite loop when date is not defined
         */
        setIsDateInputInvalidDate(false);

        // Checking for initDate !== undefined so that we are not calling onDateChange continuously which causes infinite loop
        if (!isDateInputValueUndefined && date !== undefined && date !== null) {
          setIsDateInputValueUndefined(true);
          console.warn("date undefined");
          onDateChange?.(undefined);
          return;
        } else if (isDateInputValueUndefined) {
          return;
        }
      }
    };

    return (
      <DateInputField
        onChange={handleDateChange}
        value={date}
        disabled={disabled}
        onBlur={handleBlur}
        onFocus={setFocus}
        format={format}
        showDateAsText={showDateAsText}
        label={label}
        className={className}
        status={status}
        statusMessage={statusMessage || error}
        displayDateTextMethod={displayDateTextMethod}
        YYYYPlaceholderText={YYYYPlaceholderText}
        MMPlaceholderText={MMPlaceholderText}
        DDPlaceholderText={DDPlaceholderText}
        DDAriaLabel={DDAriaLabel}
        MMAriaLabel={MMAriaLabel}
        YYYYAriaLabel={YYYYAriaLabel}
        daysDisabled={daysDisabled}
        validateMethod={validateMethod}
        onValidate={onValidate}
        minDate={minDate}
        maxDate={maxDate}
        daysDisabledErrorLabel={daysDisabledErrorLabel}
        maxDateErrorLabel={maxDateErrorLabel}
        minDateErrorLabel={minDateErrorLabel}
        ref={ref}
        labelRef={labelRef}
        separator={separator}
        _privateProps={{
          isCalendarOpen: activeIcon,
          setHasDateInputDisabledDate,
        }}
      >
        {disabled ? (
          <button
            disabled={disabled}
            className="spark-input__addon spark-date__calendar-toggle"
            aria-label={ariaCalendarPopoverLabel}
            style={{ cursor: "not-allowed" }}
            type="button"
          >
            <i
              data-testid="calendar-toggle-button-icon"
              className={iconClassName}
            />
          </button>
        ) : (
          <CalendarPopover
            calOpen={calOpen}
            popoverRef={popoverRef}
            setCalOpen={setCalOpen}
            role="dialog"
            visibleCount={visibleCount}
            isRangeVariant={isRangeControlled.current}
            anchorElement={
              <a
                role="button"
                tabIndex={0}
                className="spark-date__calendar-toggle spark-date__calendar-toggle-link"
                aria-label={ariaCalendarPopoverLabel}
              >
                <i
                  ref={iconRef}
                  data-testid="calendar-toggle-button-icon"
                  className={iconClassName}
                />
              </a>
            }
            onClickAnchor={setActiveIcon}
            unmountEvent={(): void => {
              const parentNode = iconRef.current?.parentNode;
              if (parentNode !== null) {
                (parentNode as HTMLElement)?.focus?.();
              }
            }}
            anchorX={anchorX}
            anchorY={anchorY}
            portalWrapperClassName={portalWrapperClassName}
          >
            <Calendar
              value={
                isRangeControlled.current
                  ? undefined
                  : hasDateInputDisabledDate
                  ? null
                  : handleInvalidDate(date)
              }
              onChange={handleDatePickerSelection}
              range={range}
              onRangeChange={(date): void => {
                // Set flag for second date input, so it could open Calendar automatically
                const parentElement = labelRef.current?.parentElement;
                parentElement?.setAttribute("data-change-source", "calendar");
                // Remove flag after 1 sec
                setTimeout(
                  () => parentElement?.removeAttribute("data-change-source"),
                  1000
                );

                prevRange.current = range;
                onRangeChange?.(date);
              }}
              minDate={minDate}
              maxDate={maxDate}
              advanceByMonthAndYear={advanceByMonthAndYear}
              monthsList={monthsList}
              yearsList={yearsList}
              disabled={disabled}
              visibleCount={visibleCount}
              notes={notes}
              daysDisabled={daysDisabled}
              showTodayButton={showTodayButton}
              showTodayButtonLabel={showTodayButtonLabel}
              hideDaysOutsideCurrentMonth={hideDaysOutsideCurrentMonth}
              ariaNextMonthLabel={ariaNextMonthLabel}
              ariaPreviousMonthLabel={ariaPreviousMonthLabel}
              calendarMonthsNavAriaLabel={calendarMonthsNavAriaLabel}
              navigateToDisabledMonths={navigateToDisabledMonths}
              _privateProps={{
                isInline: false,
                isActive: active,
                popoverRef,
                startDateSetterOnly: isRangeStartInput,
                endDateSetterOnly: isRangeEndInput,
              }}
            />
          </CalendarPopover>
        )}
      </DateInputField>
    );
  }
);

DateInputWithPicker.displayName = "DateInputWithPicker";
